import { HARDWARE_MB_PER_GB, parseCapacityLabelToGbRange } from './hardware-capacity-gb'

/**
 * 终端硬件内存 / 硬盘档位与接口 memory_capacity_*、whole_disk_total_*（单位 MB）的固定映射。
 * 与统计、筛选下拉、饼图共用同一套文案，不再从文案解析或相邻档位推算区间。
 */

/** 与旧逻辑一致：100TB（MB）作为「及以上」档的上限 */
const CAPACITY_MB_UPPER = 100 * 1024 * 1024

export const HARDWARE_MEMORY_SPEC_LABELS = [
  '<8GB',
  '8GB-16GB',
  '16GB-32GB',
  '32GB-64GB',
  '64GB-128GB',
  '>=128GB',
] as const

export const HARDWARE_DISK_CAPACITY_LABELS = [
  '<512GB',
  '512GB-1TB',
  '1TB-2TB',
  '2TB-3TB',
  '4TB-8TB',
  '>=8TB',
] as const

export type HardwareMemorySpecLabel = (typeof HARDWARE_MEMORY_SPEC_LABELS)[number]
export type HardwareDiskCapacityLabel = (typeof HARDWARE_DISK_CAPACITY_LABELS)[number]

const MEMORY_SPEC_MB: Record<HardwareMemorySpecLabel, { minMb: number; maxMb: number }> = {
  '<8GB': { minMb: 0, maxMb: 8191 },
  '8GB-16GB': { minMb: 8192, maxMb: 16383 },
  '16GB-32GB': { minMb: 16384, maxMb: 32767 },
  '32GB-64GB': { minMb: 32768, maxMb: 65535 },
  '64GB-128GB': { minMb: 65536, maxMb: 131071 },
  '>=128GB': { minMb: 131072, maxMb: CAPACITY_MB_UPPER },
}

const DISK_CAPACITY_MB: Record<HardwareDiskCapacityLabel, { minMb: number; maxMb: number }> = {
  '<512GB': { minMb: 0, maxMb: 524287 },
  '512GB-1TB': { minMb: 524288, maxMb: 1048575 },
  '1TB-2TB': { minMb: 1048576, maxMb: 2097151 },
  '2TB-3TB': { minMb: 2097152, maxMb: 4194303 },
  '4TB-8TB': { minMb: 4194304, maxMb: 8388607 },
  '>=8TB': { minMb: 8388608, maxMb: CAPACITY_MB_UPPER },
}

export function resolveHardwareMemorySpecRangeMb (
  label: string | undefined
): { minMb: number; maxMb: number } | null {
  if (label == null || label === '') return null
  const row = MEMORY_SPEC_MB[label as HardwareMemorySpecLabel]
  return row ?? null
}

export function resolveHardwareDiskCapacityRangeMb (
  label: string | undefined
): { minMb: number; maxMb: number } | null {
  if (label == null || label === '') return null
  const row = DISK_CAPACITY_MB[label as HardwareDiskCapacityLabel]
  return row ?? null
}

/** 整盘总容量（MB）映射到固定硬盘档位（与下拉 `disk_capacity` 一致） */
export function mapWholeDiskMbToDiskCapacityLabel (totalMb: number): HardwareDiskCapacityLabel | undefined {
  if (!Number.isFinite(totalMb) || totalMb < 0) return undefined
  const mb = Math.round(totalMb)
  for (const label of HARDWARE_DISK_CAPACITY_LABELS) {
    const row = DISK_CAPACITY_MB[label]
    if (mb >= row.minMb && mb <= row.maxMb) return label
  }
  return undefined
}

/** 内存总容量（MB）映射到固定内存档位（与下拉 `memory_spec` 一致） */
export function mapMemoryMbToMemorySpecLabel (totalMb: number): HardwareMemorySpecLabel | undefined {
  if (!Number.isFinite(totalMb) || totalMb < 0) return undefined
  const mb = Math.round(totalMb)
  for (const label of HARDWARE_MEMORY_SPEC_LABELS) {
    const row = MEMORY_SPEC_MB[label]
    if (mb >= row.minMb && mb <= row.maxMb) return label
  }
  return undefined
}

/**
 * 饼图扇区 name → 筛选下拉档位：已是固定文案则原样返回；否则解析容量后按 MB 区间落入对应档位。
 * （接口可能下发 `16GB`、`1TB` 等，无 `<` / 与下拉不完全一致时需映射。）
 */
export function resolveDiskSliceNameToDropdownLabel (sliceName: string): HardwareDiskCapacityLabel | undefined {
  const s = String(sliceName).trim()
  if (!s || s === 'Unknown') return undefined
  if ((HARDWARE_DISK_CAPACITY_LABELS as readonly string[]).includes(s)) {
    return s as HardwareDiskCapacityLabel
  }
  const r = parseCapacityLabelToGbRange(s)
  if (!r) return undefined
  const midGb = (r.minGb + r.maxGb) / 2
  const mb = Math.round(midGb * HARDWARE_MB_PER_GB)
  return mapWholeDiskMbToDiskCapacityLabel(mb)
}

export function resolveMemorySliceNameToDropdownLabel (sliceName: string): HardwareMemorySpecLabel | undefined {
  const s = String(sliceName).trim()
  if (!s || s === 'Unknown') return undefined
  if ((HARDWARE_MEMORY_SPEC_LABELS as readonly string[]).includes(s)) {
    return s as HardwareMemorySpecLabel
  }
  const r = parseCapacityLabelToGbRange(s)
  if (!r) return undefined
  const midGb = (r.minGb + r.maxGb) / 2
  const mb = Math.round(midGb * HARDWARE_MB_PER_GB)
  return mapMemoryMbToMemorySpecLabel(mb)
}
